/**
 * Redes de monitoreo hashtag (registro + scrape). Catálogo Core: INSTAGRAM, TIKTOK, YOUTUBE, TWITTER.
 * Sin Facebook.
 */
export const DEFAULT_HASHTAG_MONITORING_NETWORK_CODES = [
  'youtube',
  'tiktok',
  'x',
  'instagram',
] as const;

export type HashtagMonitoringNetworkCode =
  (typeof DEFAULT_HASHTAG_MONITORING_NETWORK_CODES)[number];

/** Mismo set al registrar (POST /hashtags/campaigns) y al filtrar filas para scrape. */
export const DEFAULT_HASHTAG_REGISTER_NETWORK_CODES = DEFAULT_HASHTAG_MONITORING_NETWORK_CODES;

/** Nunca enviar al crear campaña (producto no monitorea hashtags en Facebook). */
const EXCLUDED_CORE_PLATFORM_CODES = new Set(['FACEBOOK', 'FB']);

/**
 * Core `social_media_platform.platform_code` (MAYÚSCULAS; el repo hace strip + upper).
 * X en UI → TWITTER en BD.
 */
const FE_CODE_TO_CORE: Record<string, string> = {
  youtube: 'YOUTUBE',
  yt: 'YOUTUBE',
  tiktok: 'TIKTOK',
  instagram: 'INSTAGRAM',
  ig: 'INSTAGRAM',
  x: 'TWITTER',
  twitter: 'TWITTER',
  facebook: 'FACEBOOK',
  fb: 'FACEBOOK',
};

/** Convierte códigos FE al enum de Core (sin duplicados ni Facebook). */
export function toCoreHashtagPlatformCodes(codes: readonly string[]): string[] {
  const out: string[] = [];
  for (const code of codes) {
    const trimmed = String(code ?? '').trim();
    if (!trimmed) continue;
    const core =
      FE_CODE_TO_CORE[trimmed.toLowerCase()] ??
      (trimmed === trimmed.toUpperCase() ? trimmed : trimmed.toUpperCase());
    if (EXCLUDED_CORE_PLATFORM_CODES.has(core)) continue;
    if (!out.includes(core)) out.push(core);
  }
  return out;
}

/** platform_codes por defecto: YOUTUBE, TIKTOK, TWITTER, INSTAGRAM. */
export function defaultCoreHashtagRegisterPlatformCodes(): string[] {
  return toCoreHashtagPlatformCodes(DEFAULT_HASHTAG_REGISTER_NETWORK_CODES);
}

/** @deprecated Usar defaultCoreHashtagRegisterPlatformCodes() */
export function defaultCoreHashtagPlatformCodes(): string[] {
  return defaultCoreHashtagRegisterPlatformCodes();
}

function registerNetworkCodesSource(platformCodes?: string[]): readonly string[] {
  const fromEnv = process.env.REACT_APP_HASHTAG_REGISTER_PLATFORM_CODES?.split(',')
    .map(s => s.trim())
    .filter(Boolean);
  if (fromEnv?.length) return fromEnv;
  if (platformCodes?.length) return platformCodes;
  return DEFAULT_HASHTAG_REGISTER_NETWORK_CODES;
}

/** Payload POST /api/tenant/hashtags/campaigns */
export function buildHashtagCampaignCreatePayload(
  name: string,
  platformCodes?: string[]
): { name: string; platform_codes: string[] } {
  return {
    name: name.trim().replace(/^#/, ''),
    platform_codes: toCoreHashtagPlatformCodes(registerNetworkCodesSource(platformCodes)),
  };
}

/** @deprecated Usar defaultCoreHashtagRegisterPlatformCodes() */
export const DEFAULT_HASHTAG_MONITORING_PLATFORM_CODES_API =
  defaultCoreHashtagRegisterPlatformCodes();

/** @deprecated Usar DEFAULT_HASHTAG_MONITORING_NETWORK_CODES */
export const HASHTAG_SCRAPE_NETWORK_CODES = DEFAULT_HASHTAG_MONITORING_NETWORK_CODES;
